/**
 * Modal "Registrar saldo bancario" (Sprint 17 Fase 2).
 *
 * El tesorero ingresa el saldo según banco (lo que ve en la
 * aplicación bancaria). El modal muestra en vivo el saldo calculado
 * por el sistema + la diferencia. Diferencia cero = cuadrado;
 * diferencia distinta de cero = hay movimiento no registrado.
 */
import { useEffect, useState } from 'react';
import {
  crearSaldoBancario,
  getSaldoCalculadoHoy,
  type SaldoBancarioRead,
  type SaldoCalculadoHoyResponse,
} from '../../lib/api/saldos-bancarios';
import { formatCLP } from '../../lib/format';
import { Monto, Num } from './Monto';

export interface ModalRegistrarSaldoProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (saldo: SaldoBancarioRead) => void;
  /**
   * Datos del sistema (opcional). Si no se pasan, el modal hace fetch
   * al abrirse. Permite preview con mock data en /dev.
   */
  calculadoInicial?: SaldoCalculadoHoyResponse | null;
}

export function ModalRegistrarSaldo({
  open,
  onClose,
  onSuccess,
  calculadoInicial,
}: ModalRegistrarSaldoProps) {
  const [calculado, setCalculado] = useState<SaldoCalculadoHoyResponse | null>(
    calculadoInicial ?? null,
  );
  const [saldoBanco, setSaldoBanco] = useState<string>('');
  const [notas, setNotas] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSaldoBanco('');
    setNotas('');
    setError(null);
    if (!calculadoInicial) {
      getSaldoCalculadoHoy()
        .then(setCalculado)
        .catch((e) =>
          setError(e instanceof Error ? e.message : 'Error al cargar saldo'),
        );
    } else {
      setCalculado(calculadoInicial);
    }
  }, [open, calculadoInicial]);

  if (!open) return null;

  const saldoNum = Number(saldoBanco.replace(/[^\d-]/g, '')) || 0;
  const tieneCalculo = calculado && !calculado.sin_apertura;
  const diferencia = tieneCalculo
    ? saldoNum - (calculado!.saldo_calculado ?? 0)
    : 0;
  const disabled = submitting || saldoBanco === '' || saldoNum < 0;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const result = await crearSaldoBancario({
        saldo_banco: saldoNum,
        notas: notas.trim() || null,
      });
      onSuccess(result);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al registrar');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-registrar-saldo-titulo"
    >
      <div
        className="bg-bg-card border border-white/[0.08] rounded-lg max-w-md w-full p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2
              id="modal-registrar-saldo-titulo"
              className="text-lg font-medium text-text-primary"
            >
              Registrar saldo bancario
            </h2>
            <p className="text-xs text-text-muted mt-1">
              Ingresa el saldo según la aplicación del banco.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-text-muted hover:text-text-primary p-1 -mr-1"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        {/* Saldo calculado por el sistema */}
        <div className="bg-bg-sunken border border-white/[0.06] rounded p-3 mb-4">
          {calculado === null && (
            <div className="text-xs text-text-muted">Cargando…</div>
          )}
          {calculado?.sin_apertura && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-text-muted">
                Sin saldo de apertura
              </div>
              <p className="text-xs text-text-secondary mt-1 leading-relaxed">
                Aún no se ha sembrado el saldo de apertura del año, así que el
                sistema no puede calcular un saldo esperado. Tu registro queda
                como referencia.
              </p>
            </div>
          )}
          {calculado && !calculado.sin_apertura && (
            <>
              <div className="text-[10px] uppercase tracking-wide text-text-muted">
                Saldo calculado por el sistema
              </div>
              <div className="text-xl font-medium text-text-primary mt-1">
                <Monto value={calculado.saldo_calculado ?? 0} />
              </div>
              <div className="text-[11px] text-text-muted mt-2 space-y-0.5">
                <div>
                  Apertura {calculado.anio}:{' '}
                  <span className="text-text-secondary">
                    <Monto value={calculado.apertura ?? 0} />
                  </span>
                </div>
                <div>
                  Desde el 1-ene:{' '}
                  <span className="text-emerald-400">
                    <Num>+{formatCLP(calculado.ingresos_anio)}</Num>
                  </span>{' '}
                  ingresos /{' '}
                  <span className="text-red-400">
                    <Num>−{formatCLP(calculado.gastos_anio)}</Num>
                  </span>{' '}
                  gastos
                </div>
              </div>
            </>
          )}
        </div>

        {/* Input saldo banco */}
        <label className="block text-xs text-text-muted mb-1">
          Saldo según banco
        </label>
        <input
          type="number"
          inputMode="numeric"
          value={saldoBanco}
          onChange={(e) => setSaldoBanco(e.target.value)}
          placeholder="0"
          autoFocus
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-base focus:outline-none focus:border-orange"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        />

        {/* Diferencia en vivo */}
        {tieneCalculo && saldoBanco !== '' && (
          <div
            className={`mt-3 p-3 rounded ${
              diferencia === 0
                ? 'bg-emerald-500/10 border border-emerald-500/30'
                : 'bg-amber-500/10 border border-amber-500/30'
            }`}
          >
            <div className="flex items-baseline justify-between">
              <span className="text-xs text-text-secondary">Diferencia</span>
              <Num
                className={`text-base font-medium ${
                  diferencia === 0 ? 'text-emerald-400' : 'text-amber-400'
                }`}
              >
                {diferencia >= 0 ? '+' : ''}
                {formatCLP(diferencia)}
              </Num>
            </div>
            {diferencia !== 0 && (
              <p className="text-[11px] text-text-muted mt-1.5 leading-relaxed">
                {diferencia > 0
                  ? 'Banco tiene más de lo esperado: posible ingreso no registrado (pago, transferencia).'
                  : 'Sistema tiene más de lo esperado: posible gasto no registrado.'}
              </p>
            )}
          </div>
        )}

        {/* Notas */}
        <label className="block text-xs text-text-muted mt-4 mb-1">
          Notas (opcional)
        </label>
        <textarea
          value={notas}
          onChange={(e) => setNotas(e.target.value)}
          rows={2}
          placeholder="Cualquier observación relevante…"
          className="w-full bg-bg-sunken border border-white/[0.08] rounded px-3 py-2 text-text-primary text-sm focus:outline-none focus:border-orange resize-none"
        />

        {error && (
          <div className="mt-3 text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded p-2">
            {error}
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex-1 px-3 py-2 text-sm text-text-secondary border border-white/[0.08] rounded hover:text-text-primary hover:border-white/[0.16]"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={disabled}
            className="flex-1 px-3 py-2 text-sm font-medium bg-orange text-white rounded hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? 'Registrando…' : 'Registrar saldo'}
          </button>
        </div>
      </div>
    </div>
  );
}
